import { BALANCE, BUILDING_DEFS, colOf } from '../constants';
import type { Simulation } from '../sim';
import { GOOD_KINDS, ORE_GOLD, ORE_IRON } from '../types';
import { sendGoodsCargo, sendToDepot } from './jobs';

/** Load ore at piles, deposit at the depot. */
export function stepHauling(sim: Simulation): void {
  const state = sim.state;
  for (const peep of state.peeps) {
    if (peep.task.kind === 'toPile' && peep.path === null) {
      const col = peep.task.col;
      const pile = state.piles.get(col);
      if (!pile) {
        peep.task = { kind: 'idle' };
        continue;
      }
      const standCol = colOf(Math.floor(peep.x), Math.floor(peep.y));
      if (standCol !== col) {
        pile.claimedDt = 0;
        peep.task = { kind: 'idle' };
        continue;
      }
      // Take the more valuable ore first; one type per trip.
      const takeGold = pile.goldDt >= pile.ironDt ? pile.goldDt > 0 : false;
      const available = takeGold ? pile.goldDt : pile.ironDt;
      const take = Math.min(BALANCE.haulerCarryDt, available);
      if (take <= 0) {
        pile.claimedDt = 0;
        state.piles.delete(col);
        peep.task = { kind: 'idle' };
        continue;
      }
      if (takeGold) pile.goldDt -= take;
      else pile.ironDt -= take;
      peep.carryDt = take;
      peep.carryType = takeGold ? ORE_GOLD : ORE_IRON;
      pile.claimedDt = 0;
      if (pile.ironDt + pile.goldDt <= 0) state.piles.delete(col);
      if (!sendToDepot(sim, peep)) peep.task = { kind: 'idle' };
    } else if (peep.task.kind === 'toDepot' && peep.path === null) {
      const depotState = state.economy.depot;
      const space = depotState.capacityDt - depotState.ironDt - depotState.goldDt;
      const deposit = Math.min(peep.carryDt, Math.max(0, space));
      if (deposit > 0) {
        if (peep.carryType === ORE_GOLD) depotState.goldDt += deposit;
        else depotState.ironDt += deposit;
        peep.carryDt -= deposit;
      }
      if (peep.carryDt <= 0) {
        peep.carryDt = 0;
        peep.carryType = 0;
      }
      peep.task = { kind: 'idle' };
    } else if (peep.task.kind === 'toGoodsPile' && peep.path === null) {
      const col = peep.task.col;
      const pile = state.goodsPiles.get(col);
      if (!pile) {
        peep.task = { kind: 'idle' };
        continue;
      }
      const standCol = colOf(Math.floor(peep.x), Math.floor(peep.y));
      if (standCol !== col) {
        pile.claimedUnits = 0;
        peep.task = { kind: 'idle' };
        continue;
      }
      const take = Math.min(BALANCE.goodsHaulerCarryUnits, pile.units);
      pile.claimedUnits = 0;
      if (take <= 0) {
        state.goodsPiles.delete(col);
        peep.task = { kind: 'idle' };
        continue;
      }
      pile.units -= take;
      if (pile.units <= 0) state.goodsPiles.delete(col);
      peep.carryDt = take;
      peep.carryGood = pile.good;
      // No destination right now: keep the cargo, go idle, retry next assign.
      if (!sendGoodsCargo(sim, peep)) peep.task = { kind: 'idle' };
    } else if (peep.task.kind === 'toOutputPickup' && peep.path === null) {
      // Arrived beside the producer: draw from its output store. Adjacency
      // isn't re-verified, matching the toGoodsDropoff convention.
      const buildingId = peep.task.buildingId;
      const building = state.buildings.find((candidate) => candidate.id === buildingId);
      if (!building) {
        peep.task = { kind: 'idle' };
        continue;
      }
      building.outputClaimedUnits = 0;
      const produces = BUILDING_DEFS[building.type].produces;
      const take = produces ? Math.min(BALANCE.goodsHaulerCarryUnits, building.outputUnits) : 0;
      if (take <= 0) {
        peep.task = { kind: 'idle' };
        continue;
      }
      building.outputUnits -= take;
      peep.carryDt = take;
      peep.carryGood = produces!.good;
      if (!sendGoodsCargo(sim, peep)) peep.task = { kind: 'idle' };
    } else if (peep.task.kind === 'toGoodsDropoff' && peep.path === null) {
      const buildingId = peep.task.buildingId;
      const building = state.buildings.find((candidate) => candidate.id === buildingId);
      const good = peep.carryGood;
      if (!building || good === null) {
        if (good === null) peep.carryDt = 0;
        peep.task = { kind: 'idle' };
        continue;
      }
      const def = BUILDING_DEFS[building.type];
      if (def.consumes && def.consumes.good === good) {
        const space = def.consumes.bufferUnits - building.inputUnits;
        const deposit = Math.min(peep.carryDt, Math.max(0, space));
        building.inputUnits += deposit;
        peep.carryDt -= deposit;
      } else if (def.goodsCapacityUnits !== undefined) {
        let stored = 0;
        for (const kind of GOOD_KINDS) stored += state.economy.goodsDepot.units[kind];
        const space = state.economy.goodsDepot.capacityUnits - stored;
        const deposit = Math.min(peep.carryDt, Math.max(0, space));
        state.economy.goodsDepot.units[good] += deposit;
        peep.carryDt -= deposit;
      }
      if (peep.carryDt <= 0) {
        peep.carryDt = 0;
        peep.carryGood = null;
      }
      // Leftover cargo (buffer/depot filled up mid-walk) rides along until
      // the next assign pass finds it a new home.
      peep.task = { kind: 'idle' };
    }
  }
}
